export const RECIPE_SERVING_COUNT = 10;

export function findMenuFoodItemById(menuItems, foodItemId) {
    const id = Number(foodItemId);
    if (!id || !menuItems || typeof menuItems !== 'object') return null;
    for (const list of Object.values(menuItems)) {
        if (!Array.isArray(list)) continue;
        const row = list.find(
            (it) => Number(it.Food_Item_Id ?? it.foodItemId ?? it.cateringItemId) === id
        );
        if (row) return row;
    }
    return null;
}

export function recipeResponseHasContent(data) {
    if (!data) return false;
    return (data.items?.length > 0) || (data.steps?.length > 0);
}

/** Ingredient names from GET /restaurants/food-items/recipes (inventory recipe lines). */
export function recipeIngredientsListFromResponse(data) {
    if (!data?.items?.length) return [];
    return data.items
        .map(
            (it) =>
                it.inventoryItemName ??
                it.Inventory_Item_Name ??
                it.itemName ??
                it.Item_Name ??
                ''
        )
        .map((name) => String(name).trim())
        .filter(Boolean);
}

export function resolveRecipeFoodItemName(menuItems, foodItemId, recipeResult) {
    const fromApi =
        recipeResult?.foodItemName ??
        recipeResult?.Food_Item_Name ??
        recipeResult?.foodItem ??
        recipeResult?.Food_Item;
    if (fromApi != null && String(fromApi).trim()) return String(fromApi).trim();
    const row = findMenuFoodItemById(menuItems, foodItemId);
    const fromMenu = row?.Food_Item ?? row?.foodItem ?? row?.itemName ?? row?.Item_Name;
    if (fromMenu != null && String(fromMenu).trim()) return String(fromMenu).trim();
    return '';
}

export function getRecipeServingNoteText(pageMode, isRecipeUpdate) {
    if (pageMode === 'view') {
        return `Ingredient quantities and cooking steps are for a recipe that serves ${RECIPE_SERVING_COUNT} people.`;
    }
    if (isRecipeUpdate) {
        return `Update ingredient quantities and cooking steps for a recipe that serves ${RECIPE_SERVING_COUNT} people.`;
    }
    return `Add ingredient quantities and cooking steps for a recipe that serves ${RECIPE_SERVING_COUNT} people.`;
}

export function buildRecipeIdPayload(menuTypeFilter, menuItemId) {
    return menuTypeFilter === 'catering'
        ? { cateringItemId: menuItemId }
        : { foodItemId: menuItemId };
}

/** Split step text into readable lines (newlines + inline numbered sub-steps). */
export function formatRecipeInstructionLines(instruction) {
    const text = String(instruction ?? '').trim();
    if (!text) return [];

    const byNewline = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    if (byNewline.length > 1) return byNewline;

    const byNumberedParts = text
        .split(/(?:\s+)(?=\d+[.)]\s*\S)/)
        .map((part) => part.trim())
        .filter(Boolean);
    if (byNumberedParts.length > 1) return byNumberedParts;

    return [text];
}

export function mapRecipeDataToForm(recipeData) {
    const items = (recipeData?.items || [])
        .map((it) => ({
            recipeId: it.recipeId ?? it.Recipe_Id ?? null,
            inventoryItemId: Number(it.inventoryItemId ?? it.Inventory_Item_Id),
            quantity: Number(it.quantity ?? it.Quantity),
        }))
        .filter(
            (it) =>
                it.inventoryItemId > 0 &&
                !Number.isNaN(it.quantity) &&
                it.quantity >= 0
        );

    const steps = (recipeData?.steps || [])
        .map((st, idx) => ({
            stepId: st.stepId ?? st.Step_Id ?? null,
            instruction: String(st.instruction ?? st.Instruction ?? '').trim(),
            stepNumber: st.stepNumber != null ? Number(st.stepNumber) : idx + 1,
            notes: st.notes ?? st.Notes ?? '',
        }))
        .filter((st) => st.instruction);

    return { items, steps };
}
